import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import moment from 'moment';

// Style
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './UserManagement.css';
import cs from '../../../components/commonStyle.css';

import CommonTable from '../../CommonTable/CommonTable';
import TableAction from '../../CommonTable/TableAction';
import CustomPagination from '../../CustomPagination';
import usersQuery from './usersQuery.graphql';
// Translation
import messages from '../../../locale/messages';
import { updateBanServiceHistoryStatus } from '../../../actions/siteadmin/updateBanServiceHistoryStatus';
import { deleteUser, updateUserEmail } from '../../../actions/siteadmin/users';
import EditUserEmailModal from '../EditUserEmailModal';
import history from '../../../core/history';
import {
  ALLOWED_RECENT_LIMITS,
  recentLimitToQueryParam,
} from '../../../routes/siteadmin/users/usersRecentLimit';
import {
  ALLOWED_DEVICE_FILTERS,
  buildUsersQueryString,
} from '../../../routes/siteadmin/users/usersDeviceFilter';

class UserManagement extends React.Component {
  static propTypes = {
    userManagement: PropTypes.array,
    editUser: PropTypes.any,
    deleteUser: PropTypes.any,
    title: PropTypes.string.isRequired,
    updateBanServiceHistoryStatus: PropTypes.any.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      value: '',
      currentPage: 1,
      searchList: '',
      typing: false,
      typingTimeout: 0,
      emailEditUser: null,
      emailUpdateSubmitting: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.paginationData = this.paginationData.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleRecentLimitChange = this.handleRecentLimitChange.bind(this);
    this.handleDeviceFilterChange = this.handleDeviceFilterChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    // Refetch when parent updates recentLimit/deviceFilter from URL (history listener).
    const { userManagement: { refetch }, currentPage, searchList, recentLimit, deviceFilter } = this.props;
    if (
      prevProps.recentLimit !== recentLimit
      || prevProps.deviceFilter !== deviceFilter
      || prevProps.currentPage !== currentPage
      || prevProps.searchList !== searchList
    ) {
      refetch({ currentPage, searchList, recentLimit, deviceFilter });
    }
  }

  queryVariables() {
    const { currentPage, searchList, recentLimit, deviceFilter } = this.props;
    return { currentPage, searchList, recentLimit, deviceFilter };
  }

  pushUsersUrl(recentLimit, deviceFilter) {
    const qs = buildUsersQueryString({ recentLimit, deviceFilter });
    if (history) {
      history.push(`/siteadmin/users${qs}`);
    }
  }

  async handleChange(e, userId, userMail, userName) {
    const { updateBanServiceHistoryStatus, adminMail } = this.props;
    const { currentPage, searchList } = this.props;
    const banStatus = e.target.value;
    await updateBanServiceHistoryStatus(userId, banStatus, userMail, userName, currentPage, searchList, adminMail);
  }

  paginationData(currentPage) {
    const { userManagement: { refetch }, setStateVariable } = this.props;
    const variables = { ...this.queryVariables(), currentPage };
    setStateVariable(variables);
    refetch(variables);
  }

  handleClick(searchList) {
    const { userManagement: { refetch }, setStateVariable } = this.props;
    const variables = {
      ...this.queryVariables(),
      currentPage: 1,
      searchList,
    };
    this.setState({ currentPage: 1 });
    setStateVariable(variables);
    refetch(variables);
  }

  handleRecentLimitChange(e) {
    const { deviceFilter } = this.props;
    const value = e.target.value;
    // "All" → bare /siteadmin/users; "Newest N" → ?recentLimit=N
    if (!value) {
      this.pushUsersUrl(null, deviceFilter);
      return;
    }
    const recentLimit = parseInt(value, 10);
    if (recentLimitToQueryParam(recentLimit)) {
      this.pushUsersUrl(recentLimit, deviceFilter);
    }
  }

  handleDeviceFilterChange(e) {
    const { recentLimit } = this.props;
    const deviceFilter = e.target.value || null;
    this.pushUsersUrl(recentLimit, deviceFilter || null);
  }

  handleSearchChange = (e) => {
    const self = this;
    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }
    self.setState({
      searchList: e.target.value,
      typing: false,
      typingTimeout: setTimeout(function () {
        self.handleClick(self.state.searchList);
      }, 450),
    });
  };

  openEmailEdit = (user) => {
    this.setState({
      emailEditUser: {
        id: user.id,
        email: user.email,
      },
    });
  };

  closeEmailEdit = () => {
    this.setState({ emailEditUser: null, emailUpdateSubmitting: false });
  };

  handleEmailUpdate = async (userId, email) => {
    const { updateUserEmail, userManagement: { refetch } } = this.props;
    this.setState({ emailUpdateSubmitting: true });
    const result = await updateUserEmail(userId, email);
    this.setState({ emailUpdateSubmitting: false });
    if (result?.status === 'success') {
      this.closeEmailEdit();
      await refetch(this.queryVariables());
    }
  };

  async deleteChange(id, profileId, userType) {
    const { deleteUser } = this.props;
    const { userManagement: { refetch }, setStateVariable } = this.props;
    const variables = { ...this.queryVariables(), currentPage: 1 };
    await deleteUser(id, profileId, userType);
    this.setState({ currentPage: 1 });
    await setStateVariable(variables);
    await refetch(variables);
  }

  thead = () => {
    const { formatMessage } = this.props.intl;
    return [
      { data: formatMessage(messages.idLabel) },
      { data: formatMessage(messages.name) },
      { data: formatMessage(messages.emailLabel) },
      { data: formatMessage(messages.phoneLabel) },
      { data: formatMessage(messages.createdDate) },
      { data: formatMessage(messages.actionLabel) },
    ];
  };

  tbody = () => {
    const { userManagement: { userManagement } } = this.props;
    const { formatMessage } = this.props.intl;

    return userManagement?.usersData.map((value) => {
      return {
        id: value?.id,
        data: [
          { data: value?.profile?.profileId },
          {
            data:
              <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween)}>
                <a href={'/siteadmin/profileView/' + ((value.profile) ? value.profile.profileId : '')}>
                  {value?.profile?.firstName}
                </a>
                <TableAction
                  onClickDelete={() => this.deleteChange(value.id)}
                  onClickEdit={() => this.openEmailEdit(value)}
                  showDelete={true}
                  showView={true}
                  showEdit={true}
                  link={'/siteadmin/profileView/' + ((value.profile) ? value.profile.profileId : '')}
                />
              </div>,
          },
          { data: value?.email },
          { data: value?.profile?.phoneNumber ? value?.profile?.phoneNumber : '-' },
          { data: moment(value?.profile?.createdAt).format('MM/DD/YYYY') },
          {
            data: <select name="userBanStatus" className={cx(s.formControlSelect, s.userVerticalAlign, s.btnMarginBottom)}
              onChange={(e) => this.handleChange(e, value.id, value.email, value?.profile?.firstName)} value={value.userBanStatus ? '1' : '0'}>
              <option value="">{formatMessage(messages.selectLabel)}</option>
              <option value="1">{formatMessage(messages.banLabel)}</option>
              <option value="0">{formatMessage(messages.unBanLabel)}</option>
            </select>,
          },
        ],
      };
    });
  };

  render() {
    const { userManagement: { userManagement } } = this.props;
    const { currentPage, searchList, recentLimit, deviceFilter } = this.props;
    const { formatMessage } = this.props.intl;
    const pageSize = recentLimit || 10;
    const recentLimitSelectValue = recentLimit && ALLOWED_RECENT_LIMITS.includes(recentLimit)
      ? String(recentLimit)
      : '';
    const deviceFilterSelectValue = deviceFilter && ALLOWED_DEVICE_FILTERS.includes(deviceFilter)
      ? deviceFilter
      : '';
      
    const { emailEditUser, emailUpdateSubmitting } = this.state;

    return (
      <div className={cx(s.pagecontentWrapper, 'nobackground')}>
        <EditUserEmailModal
          show={!!emailEditUser}
          onHide={this.closeEmailEdit}
          userId={emailEditUser?.id}
          currentEmail={emailEditUser?.email}
          onSubmit={this.handleEmailUpdate}
          submitting={emailUpdateSubmitting}
        />
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="usersRecentLimitFilter">{formatMessage(messages.adminUsersRecentLimit)}</label>
            <select
              id="usersRecentLimitFilter"
              value={recentLimitSelectValue}
              onChange={this.handleRecentLimitChange}
              className={cx(cs.formControlInput)}
            >
              {/* All: default order (oldest first). Newest N: createdAt DESC via recentLimit. */}
              <option key="all" value="">
                {formatMessage(messages.adminListingsAll)}
              </option>
              {ALLOWED_RECENT_LIMITS.map((limit) => (
                <option key={limit} value={limit}>
                  {formatMessage(messages.adminListingsNewest, { count: limit })}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="usersDeviceFilter">{formatMessage(messages.adminUsersDeviceFilter)}</label>
            <select
              id="usersDeviceFilter"
              value={deviceFilterSelectValue}
              onChange={this.handleDeviceFilterChange}
              className={cx(cs.formControlInput)}
            >
              <option key="all-devices" value="">
                {formatMessage(messages.adminUsersDeviceAll)}
              </option>
              <option value="web">{formatMessage(messages.adminUsersDeviceWeb)}</option>
              <option value="ios">{formatMessage(messages.adminUsersDeviceIos)}</option>
              <option value="android">{formatMessage(messages.adminUsersDeviceAndroid)}</option>
            </select>
          </div>
        </div>
        <CommonTable
          thead={this.thead}
          tbody={this.tbody}
          title={formatMessage(messages.manageUser)}
          isExport
          exportLink={`/export-admin-data?type=users&search=${searchList}`}
          isSearch
          onSearch={this.handleSearchChange}
        />
        {
          userManagement?.usersData?.length > 0
          && <div>
            <CustomPagination
              total={userManagement.count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={pageSize}
              change={this.paginationData}
              paginationLabel={formatMessage(messages.usersLabel)}
            />
          </div>
        }
      </div>
    );
  }
}
const mapState = (state) => ({
  adminMail: state.siteSettings.data.email,
});
const mapDispatch = {
  updateBanServiceHistoryStatus,
  deleteUser,
  updateUserEmail,
};
export default compose(
  injectIntl,
  withStyles(s, cs),
  connect(mapState, mapDispatch),
  graphql(usersQuery, {
    name: 'userManagement',
    options: (props) => ({
      variables: {
        currentPage: props.currentPage,
        searchList: props.searchList,
        recentLimit: props.recentLimit,
        deviceFilter: props.deviceFilter,
      },
      fetchPolicy: 'network-only',
    }),
  }),
)(UserManagement);